import { useEffect, useRef, useCallback, useState } from 'react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { Map as MapboxMap } from 'mapbox-gl';
import { GeoJSONPolygon } from '@signalvote/shared';
import { buildTurfDrawStyles } from './turfDrawStyles';

interface UseMapboxDrawOptions {
  enabled?: boolean;
  initialPolygon?: GeoJSONPolygon | null;
  onPolygonComplete?: (geojson: GeoJSONPolygon) => void;
  onPolygonEdit?: (geojson: GeoJSONPolygon) => void;
}

interface UseMapboxDrawReturn {
  draw: MapboxDraw | null;
  polygon: GeoJSONPolygon | null;
  isDrawing: boolean;
  clearPolygon: () => void;
  getGeoJSON: () => GeoJSONPolygon | null;
}

/**
 * Hook for managing Mapbox GL Draw for polygon drawing.
 * Replaces the Google Maps useDrawingManager hook.
 */
export function useMapboxDraw(
  map: MapboxMap | null,
  options: UseMapboxDrawOptions = {}
): UseMapboxDrawReturn {
  const { enabled = false, initialPolygon, onPolygonComplete, onPolygonEdit } = options;
  const drawRef = useRef<MapboxDraw | null>(null);
  const [polygon, setPolygon] = useState<GeoJSONPolygon | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Store callbacks in refs to avoid effect re-runs
  const onPolygonCompleteRef = useRef(onPolygonComplete);
  const onPolygonEditRef = useRef(onPolygonEdit);
  // Stabilize initialPolygon — only capture it once when enabled transitions to true
  const initialPolygonRef = useRef(initialPolygon);

  useEffect(() => {
    onPolygonCompleteRef.current = onPolygonComplete;
    onPolygonEditRef.current = onPolygonEdit;
  }, [onPolygonComplete, onPolygonEdit]);

  // Update ref when enabled changes (captures the polygon at the moment drawing starts)
  useEffect(() => {
    if (enabled) {
      initialPolygonRef.current = initialPolygon;
    }
  }, [enabled]); // intentionally omit initialPolygon — we only snapshot when enabled flips

  useEffect(() => {
    if (!map || !enabled) {
      // Clean up if disabled
      if (drawRef.current && map) {
        try {
          map.removeControl(drawRef.current);
        } catch {
          // Control may already be removed
        }
        drawRef.current = null;
      }
      setPolygon(null);
      setIsDrawing(false);
      return;
    }

    // Initialize draw control — hide built-in UI, we use TurfCutPanel instead.
    // Vertex/midpoint disks are rendered as a DOM overlay (TurfVertexOverlay);
    // the layers Draw registers here are transparent hit targets only, so its
    // native drag input still detects the right vertex.
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      defaultMode: 'draw_polygon',
      styles: buildTurfDrawStyles(),
    });

    map.addControl(draw, 'top-left');
    drawRef.current = draw;

    // Pre-load existing polygon when editing
    const initPoly = initialPolygonRef.current;
    if (initPoly) {
      const featureIds = draw.add({
        type: 'Feature',
        geometry: initPoly,
        properties: {},
      });
      if (featureIds.length > 0) {
        draw.changeMode('direct_select', { featureId: featureIds[0] });
        setPolygon(initPoly);
        setIsDrawing(false);
        onPolygonCompleteRef.current?.(initPoly);
      }
    } else {
      setIsDrawing(true);
    }

    // Event handlers
    const handleCreate = (e: MapboxDraw.DrawCreateEvent) => {
      const feature = e.features[0];
      if (feature?.geometry?.type === 'Polygon') {
        const polygonGeom: GeoJSONPolygon = {
          type: 'Polygon',
          coordinates: feature.geometry.coordinates,
        };
        setPolygon(polygonGeom);
        onPolygonCompleteRef.current?.(polygonGeom);
        setIsDrawing(false);
        // Defer mode change — calling changeMode inside draw.create causes stack overflow
        // because the previous mode's onStop re-enters during serialization
        const featureId = feature.id as string;
        requestAnimationFrame(() => {
          draw.changeMode('direct_select', { featureId });
        });
      }
    };

    const handleUpdate = (e: MapboxDraw.DrawUpdateEvent) => {
      const feature = e.features[0];
      if (feature?.geometry?.type === 'Polygon') {
        const polygonGeom: GeoJSONPolygon = {
          type: 'Polygon',
          coordinates: feature.geometry.coordinates,
        };
        setPolygon(polygonGeom);
        onPolygonEditRef.current?.(polygonGeom);
      }
    };

    const handleDelete = () => {
      setPolygon(null);
      setIsDrawing(true);
      draw.changeMode('draw_polygon');
    };

    const handleModeChange = (e: MapboxDraw.DrawModeChangeEvent) => {
      // If user clicks polygon tool button again, set drawing state
      if (e.mode === 'draw_polygon') {
        setIsDrawing(true);
      }
    };

    map.on('draw.create', handleCreate);
    map.on('draw.update', handleUpdate);
    map.on('draw.delete', handleDelete);
    map.on('draw.modechange', handleModeChange);

    return () => {
      map.off('draw.create', handleCreate);
      map.off('draw.update', handleUpdate);
      map.off('draw.delete', handleDelete);
      map.off('draw.modechange', handleModeChange);
      try {
        if (map.hasControl(draw)) {
          map.removeControl(draw);
        }
      } catch {
        // Map may be unmounted
      }
      drawRef.current = null;
    };
  }, [map, enabled]);

  const clearPolygon = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.deleteAll();
      setPolygon(null);
      setIsDrawing(true);
      drawRef.current.changeMode('draw_polygon');
    }
  }, []);

  const getGeoJSON = useCallback((): GeoJSONPolygon | null => {
    return polygon;
  }, [polygon]);

  return {
    draw: drawRef.current,
    polygon,
    isDrawing,
    clearPolygon,
    getGeoJSON,
  };
}

export default useMapboxDraw;
